import {ForecastPageData} from '@/app/interfaces/weather';
import {daysModeMaximumDaysCount} from '@/app/constants/forecast';
import {getYearPeriod} from '@/common/helpers/date/getYearPeriod';
import {BackendAPI} from '@/app/services/BackendAPI';
import {StoreState, StoreThunkDispatch} from '@/app/store';
import {createAsyncThunk} from '@/app/store/createAsyncThunk';
import {selectDomainConfig, selectHasPersonalizedPreferences, selectPreferences} from '@/app/store/config/selectors';
import {selectDeviceCoordinates, selectDeviceGeneralSettlement, selectDeviceGeolocationSettlement} from '@/app/store/geolocation/selectors'; // prettier-ignore
import {determinateExactSettlement, determinateGeneralSettlement} from '@/app/store/geolocation/actions';
import {fetchPreferencesAction} from '@/app/store/config/actions';
import config from '@config.client';

// A one spare required for case when the list contains the currently viewed location
const {mainPageTopLocationsCount} = config;

/**
 * Determine a current user's location
 * Use a user's defined default location or determines by coordinates and ip
 */
const determineLocationId = async ({
	language,
	getState,
	dispatch,
}: {
	language: string;
	getState: () => StoreState;
	dispatch: StoreThunkDispatch;
}) => {
	// Wait for personalized data loading when it is not loaded yet
	if (!selectHasPersonalizedPreferences(getState())) {
		await dispatch(fetchPreferencesAction());
	}

	const state = getState();

	// Try to use user's default location if it is defined
	const {mainPageLocationMode, defaultLocationId} = selectPreferences(state);
	if (mainPageLocationMode === 'defined' && defaultLocationId) {
		return defaultLocationId;
	}

	// Try to use determined before by device coordinates location
	const deviceGeolocationSettlement = selectDeviceGeolocationSettlement(state);
	if (deviceGeolocationSettlement) {
		return deviceGeolocationSettlement.id;
	}

	/*
	Disabled by a new business logic, temporary stay in code | (TODO remove in future)
	// Try to determinate location by an existing device coordinates
	const deviceCoordinates = selectDeviceCoordinates(state);
	if (deviceCoordinates) {
		try {
			// Fetch location by coordinates
			await dispatch(determinateExactSettlement({language}));
		} catch (error) {
			console.error(error);
		}

		const settlement = selectDeviceGeolocationSettlement(getState());
		if (settlement) {
			return settlement.id;
		}
	}*/

	// Finally use determined before or require an approximate location by server request (used IP)
	if (!selectDeviceGeneralSettlement(getState())) {
		await dispatch(determinateGeneralSettlement({language}));
	}
	return selectDeviceGeneralSettlement(getState())!.id;
};

/**
 * Fetch full set metadata used when a location in exists
 */
const fetchLocationMetadata = async ({
	language,
	locationId,
	locationCountryId,
	backendAPI,
}: {
	language: string;
	locationId: string;
	locationCountryId?: string;
	backendAPI: BackendAPI;
}) => {
	// Use cached value if allowed, it is static
	const result = await backendAPI.cachedRequest(backendAPI.misc.getPageMeta, [
		{
			locationAlternateIds: {locationId},
			locationsStableId: {ids: [locationId, locationCountryId].filter(Boolean) as string[]},
		},
	]);

	return {
		locationMeta: {
			alternateIds: result.locationAlternateIds,
			stableId: result.locationsStableId[0],
			countryStableId: result.locationsStableId[1] as string | undefined,
		},
	};
};

/**
 * Fetch an extra data: top locations with weather and resorts weather
 */
const fetchExtraData = async ({
	language,
	backendAPI,
	getState,
}: {
	language: string;
	backendAPI: BackendAPI;
	getState: () => StoreState;
}) => {
	const cachedRequest = backendAPI.cachedRequest.bind(backendAPI);
	const {getLargestSettlements, getResortLocations} = backendAPI.location;
	const {getWeatherForLocations} = backendAPI.forecast;
	const {countryId} = selectDomainConfig(getState());
	const yearPeriod = getYearPeriod();

	const [topLocationsWeather, resortsLocationsWeather] = await Promise.all([
		// Request to get list of largest locations of the domain primary country and them weather
		// Use cached value for locations list request because it is static.
		cachedRequest(getLargestSettlements, [countryId, language, mainPageTopLocationsCount, ['title', 'flags']])
			// Subsequent to get the weather
			.then(locations => getWeatherForLocations(locations, language, true)),

		// Request to get weather on actual for the current season resorts
		cachedRequest(getResortLocations, [countryId, language, 5, yearPeriod])
			// Subsequent to get the weather
			.then(locations => getWeatherForLocations(locations, language)),
	]);

	return {
		topLocationsWeather,
		resortsWeather: {
			period: yearPeriod,
			locations: resortsLocationsWeather,
		},
	};
};

/**
 * Fetch data of the main page
 * Can fetch all page data (in clientside environment) or
 * universal data only, e.g without location and forecast (used in server side mode)
 */
export const fetchMainPageForecastDataAction = createAsyncThunk<
	{
		language: string;
	},
	{
		data: ForecastPageData;
	}
>('forecast/fetchForecastPageData', async (params, {context, getState, success, dispatch}) => {
	const {language} = params;
	const {backendAPI} = context;

	// Determine a location id
	let locationId;
	if (!locationId) {
		locationId = await determineLocationId({language, getState, dispatch});
	}

	// Fetch a forecast data
	const {forecast, location} = await backendAPI.forecast.getForecast(language, locationId, daysModeMaximumDaysCount);

	// Fetch a metadata and an extra data
	const [{topLocationsWeather, resortsWeather}, {locationMeta}] = await Promise.all([
		fetchExtraData({language, getState, backendAPI}),
		fetchLocationMetadata({
			language,
			locationId: location.id,
			locationCountryId: location.superior.country?.id,
			backendAPI,
		}),
	]);

	return success({
		data: {
			language,
			locationMeta,
			location,
			forecast,
			extra: {
				type: 'mainPage',
				topLocationsWeather,
				resortsWeather,
			},
		},
	});
});
